import React, { useState } from "react";
import { globalFileServer, globalFileIcon } from "../../../../axiosInstance";
import {
  getProductImageUrl,
  getProductImageSrcSet,
  getProductImageWebpSrcSet,
  getResizedProductImageUrl,
} from "../../../utils/productImageLoader";
import ProductInfo from "../../productInfo/ProductInfo";

import { Images, Settings } from "lucide-react";
import {
  getDefaultMediaIndex,
  getDisplayableMedia,
} from "../../../utils/productMedia";
import {
  getActivePromo,
  getPromoSalePrice,
  promoShowsStrikePrice,
} from "../productPage/priceMarginUtils";
import { promoShowsCaptionBadge } from "../sales/promotionUtils";

export const StaffLocationChip = ({
  element,
  weightJump = 1,
  productLocationPopUp,
}) => {
  const availableQty = element?.availability?.availableQuantity ?? 0;
  if (availableQty >= weightJump) {
    return (
      <div
        className="location staff-on-image"
        onClick={(e) => {
          e.stopPropagation();
          productLocationPopUp?.(element);
        }}
      >
        <button
          type="button"
          className={`inventory ${
            !element.showOutOfStock ? "inventory-warn" : "gray"
          }`}
          data-tip
          data-for="registerTipLocation"
        >
          {availableQty}
        </button>
      </div>
    );
  }

  if (!element.showOutOfStock) {
    return (
      <div
        className="location staff-on-image"
        onClick={(e) => {
          e.stopPropagation();
          productLocationPopUp?.();
        }}
      >
        <button
          type="button"
          className="inventory inventory-warn"
          data-tip
          data-for="registerTipLocation"
        >
          X
        </button>
      </div>
    );
  }

  return (
    <div
      className="location staff-on-image"
      onClick={(e) => {
        e.stopPropagation();
        productLocationPopUp?.(element);
      }}
    >
      <button
        type="button"
        className="inventoryEnd-btn"
        data-tip
        data-for="registerTipLocation"
      >
        חסר
      </button>
    </div>
  );
};

const Detailes = ({
  element,
  showPrice = true,
  inCart,
  productPopUp,
  comparePricesPopUp,
  editImagesPopUp,
  editSettingsPopUp,
  srcPrev,
  showProductEdit = false,
  isStaff = false,
  weightJump = 1,
  productLocationPopUp,
  /** Small controls on the image (e.g. shopping list); keep image area clear of cart controls */
  imageCornerActions = null,
  /** Caption sale tag rendered beside the price (quantity/pack/threshold). */
  promoBadge = null,
  /** Bottom action row (cart); stops propagation so taps don’t open the product modal */
  footerSlot = null,
  /** When false, location chip is rendered by the card next to the status badge */
  showStaffLocationOnImage = true,
  alwaysShowBrand = false,
}) => {
  // Only image entries — GS1 also registers zip/mp4 assets in media[], and
  // the onError fallback walks this array by index.
  const mediaArray = getDisplayableMedia(element?.media);
  const [mediaIndex, setMediaIndex] = useState(() =>
    getDefaultMediaIndex(mediaArray),
  );
  /**
   * Per-media-index fallback stage:
   * 0 = webp <source> + jpeg srcSet, 1 = jpeg srcSet (150 1x / 300 2x),
   * 2 = 150 variant only, 3 = original image.
   * handleError inspects WHICH url failed (currentSrc) and jumps to the
   * first stage that presents a different resource — a missing webp must
   * not discard the retina jpeg, and a missing 150 must not re-present
   * itself (identical-url re-render never re-fetches, stalling the ladder).
   */
  const [variantStageByIndex, setVariantStageByIndex] = useState({});

  if (!element) return null;

  const currentMedia = mediaIndex >= 0 ? mediaArray[mediaIndex] : null;
  const originalSrc = currentMedia?.path
    ? getProductImageUrl(currentMedia.path)
    : `${globalFileServer}placeholder.jpg`;
  const variantSrc = currentMedia?.path
    ? getResizedProductImageUrl(currentMedia.path, 150)
    : null;
  const hasVariant =
    srcPrev == null && !!variantSrc && variantSrc !== originalSrc;
  const variantStage = hasVariant ? (variantStageByIndex[mediaIndex] ?? 0) : 3;

  const handleError = (event) => {
    if (variantStage < 3) {
      const failedUrl = event?.currentTarget?.currentSrc || "";
      let next;
      if (failedUrl.endsWith(".webp")) {
        next = 1; // webp sibling missing — keep the full jpeg DPR set
      } else if (failedUrl && variantSrc && failedUrl !== variantSrc) {
        next = 2; // a 2x jpeg candidate failed — the 1x may still exist
      } else {
        next = 3; // the 150 itself failed — go straight to the original
      }
      setVariantStageByIndex((prev) => ({
        ...prev,
        [mediaIndex]: Math.max(next, variantStage + 1),
      }));
      return;
    }
    if (mediaIndex > 0) {
      setMediaIndex((prev) => prev - 1);
    } else {
      setMediaIndex(-1);
    }
  };

  const imageSrc =
    srcPrev != null ? srcPrev : variantStage < 3 ? variantSrc : originalSrc;
  const canUseSrcSet = variantStage <= 1 && currentMedia?.path;
  const imageSrcSet = canUseSrcSet
    ? getProductImageSrcSet(currentMedia.path)
    : undefined;
  const webpSrcSet =
    variantStage === 0 && currentMedia?.path
      ? getProductImageWebpSrcSet(currentMedia.path)
      : undefined;

  const activePromo = getActivePromo(element);
  const salePrice = getPromoSalePrice(element.pricing?.price, activePromo);
  // Caption tags (qty/pack/threshold) replace strike+sale UI; regular FLAT/PERCENT keep it.
  // Package keeps the single-unit list price; package unit price is in the compare badge / package popup.
  const showCaptionBadge = promoShowsCaptionBadge(activePromo);
  const displaySalePrice = showCaptionBadge ? null : salePrice;
  const strikeListPrice =
    displaySalePrice != null && promoShowsStrikePrice(activePromo);

  return (
    <div className="item-card-details" onClick={productPopUp}>
      <div
        className={
          inCart
            ? "card-img-in-card"
            : (element?.availability?.availableQuantity ?? 0) > 0 ||
                element?.allowOrderWhenOos
              ? "card-img"
              : "regular-card"
        }
        style={srcPrev ? { width: "100%", maxWidth: "100%" } : undefined}
      >
        {imageCornerActions ? (
          <div
            className="item-card-details__corner"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {imageCornerActions}
          </div>
        ) : null}
        <picture>
          {webpSrcSet ? <source type="image/webp" srcSet={webpSrcSet} /> : null}
          <img
            className="img"
            loading="lazy"
            decoding="async"
            src={imageSrc}
            srcSet={imageSrcSet}
            width={currentMedia?.width || undefined}
            height={currentMedia?.height || undefined}
            onError={handleError}
            alt={element.name || "product"}
          />
        </picture>
        {element.kosherSupervision?.length > 0 &&
          element.kosherSupervision[0].supervisionAgency &&
          element.kosherSupervision[0].supervisionAgency.img?.path &&
          element.kosherSupervision[0].supervisionAgency.img.path !==
            "19.svg" && (
            <img
              className="item-card-details__kosher-badge"
              loading="lazy"
              src={
                globalFileIcon +
                element.kosherSupervision[0].supervisionAgency.img.path
              }
              alt={
                element.kosherSupervision[0].supervisionAgency.name || "כשרות"
              }
            />
          )}
        {isStaff && showStaffLocationOnImage ? (
          <StaffLocationChip
            element={element}
            weightJump={weightJump}
            productLocationPopUp={productLocationPopUp}
          />
        ) : null}
        {(editSettingsPopUp || showProductEdit) && (
          <div
            className="edit-prod-cls"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {editSettingsPopUp ? (
              <button
                type="button"
                data-tip
                data-for="registerTipDefinition"
                className="hz-icon-btn hz-icon-btn--settings"
                aria-label="הגדרות מוצר"
                onClick={editSettingsPopUp}
              >
                <span aria-hidden className="card-gear-icon">
                  <Settings size={18} strokeWidth={2} />
                </span>
              </button>
            ) : null}
          </div>
        )}
        {editImagesPopUp ? (
          <div
            className="edit-prod-images"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              data-tip
              data-for="registerTipEditImages"
              className="hz-icon-btn hz-icon-btn--images"
              aria-label="עריכת תמונות"
              onClick={editImagesPopUp}
            >
              <span aria-hidden className="card-gear-icon">
                <Images size={18} strokeWidth={2} />
              </span>
            </button>
          </div>
        ) : null}
      </div>

      <div className="desc">
        <span className="product-info">
          <ProductInfo
            selectedProd={element}
            alwaysShowBrand={alwaysShowBrand}
          />
        </span>
        <div className="kosher-and-price">
          <div className="price-leading">
            {showCaptionBadge ? (
              <div className="promo-badge-slot">{promoBadge}</div>
            ) : null}
            {displaySalePrice != null && (
              <span
                className={`current-price current-price--sale${
                  comparePricesPopUp ? " price--editable" : ""
                }`}
                onClick={(e) => {
                  if (!comparePricesPopUp) return;
                  e.stopPropagation();
                  comparePricesPopUp();
                }}
                data-tip={comparePricesPopUp ? true : undefined}
                data-for={
                  comparePricesPopUp ? "registerTipComparePrice" : undefined
                }
                role={comparePricesPopUp ? "button" : undefined}
                tabIndex={comparePricesPopUp ? 0 : undefined}
                onKeyDown={
                  comparePricesPopUp
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          comparePricesPopUp();
                        }
                      }
                    : undefined
                }
              >
                {displaySalePrice}
              </span>
            )}
          </div>
          {showPrice &&
            (element.pricing?.price != null || comparePricesPopUp) && (
              <div className="price-main-cont">
                <span
                  className={`${
                    element.pricing?.price != null
                      ? strikeListPrice
                        ? "price with-sale"
                        : "current-price"
                      : "current-price current-price--unset"
                  }${comparePricesPopUp ? " price--editable" : ""}`}
                  onClick={(e) => {
                    if (!comparePricesPopUp) return;
                    e.stopPropagation();
                    comparePricesPopUp();
                  }}
                  data-tip={comparePricesPopUp ? true : undefined}
                  data-for={
                    comparePricesPopUp ? "registerTipComparePrice" : undefined
                  }
                  role={comparePricesPopUp ? "button" : undefined}
                  tabIndex={comparePricesPopUp ? 0 : undefined}
                  onKeyDown={
                    comparePricesPopUp
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            comparePricesPopUp();
                          }
                        }
                      : undefined
                  }
                >
                  {element.pricing?.price != null ? element.pricing.price : "—"}
                </span>
              </div>
            )}
        </div>
      </div>

      {footerSlot ? (
        <div
          className="item-card-details__footer"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {footerSlot}
        </div>
      ) : null}
    </div>
  );
};

export default Detailes;
